import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import SetPassword from './SetPassword';
import LanguageToggle from './LanguageToggle';

const REC_COLORS = {
  BUY_NOW:     { color: 'var(--red)',     bg: 'var(--red-l)',     bd: 'var(--red-b)' },
  BUY_TRANCHE: { color: 'var(--amber)',   bg: 'var(--amber-l)',   bd: 'var(--amber-b)' },
  HOLD:        { color: 'var(--emerald)', bg: 'var(--emerald-l)', bd: 'var(--emerald-b)' },
  WAIT:        { color: 'var(--blue)',    bg: 'var(--blue-l)',    bd: 'var(--blue-b)' },
  MONITOR:     { color: 'var(--blue)',    bg: 'var(--blue-l)',    bd: 'var(--blue-b)' },
};
const DEFAULT_REC = { color: 'var(--blue)', bg: 'var(--blue-l)', bd: 'var(--blue-b)' };

export default function Nav({ tabs, activeTab, setActiveTab, onSave, isSaving, lastSaved, results, settings }) {
  const { t } = useTranslation();
  const { user, signOut, canSave, role } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);          // user avatar dropdown
  const [showChangePw, setShowChangePw] = useState(false);  // change password modal
  const [drawerOpen, setDrawerOpen] = useState(false);      // mobile hamburger drawer
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    if (menuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  // Close drawer with Escape key
  useEffect(() => {
    function handleEsc(e) { if (e.key === 'Escape') setDrawerOpen(false); }
    if (drawerOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [drawerOpen]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const userInitials = user?.email ? user.email.split('@')[0].slice(0, 2).toUpperCase() : '??';
  const userName = user?.email ? user.email.split('@')[0] : '';

  const sti = results?.sti?.total;
  const rec = results?.recommendation;
  const recEntry = REC_COLORS[rec?.action] || DEFAULT_REC;
  const recColor = recEntry.color;
  const recLabel = rec?.action ? t(`rec.${rec.action}`, { defaultValue: rec.label || rec.action }) : '';

  const stiColor = sti >= 75 ? 'var(--red)' : sti >= 55 ? 'var(--amber)' : 'var(--emerald)';
  const stiBg    = sti >= 75 ? 'var(--red-l)' : sti >= 55 ? 'var(--amber-l)' : 'var(--emerald-l)';
  const stiBd    = sti >= 75 ? 'var(--red-b)' : sti >= 55 ? 'var(--amber-b)' : 'var(--emerald-b)';

  // Helpers
  const tabLabel = (tab) => t(`tabs.${tab.id}`, { defaultValue: tab.label });
  const roleLabel = t(`roles.${role}`, { defaultValue: role.toUpperCase() });
  const roleColor = role === 'admin' ? 'pur' : role === 'data_updater' ? 'blu' : 'grn';

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
        height: 'var(--nav-h)',
        background: 'rgba(15,20,32,.94)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center',
        padding: '0 16px', gap: 10,
      }}>
        {/* Hamburger — mobile only */}
        <button
          onClick={() => setDrawerOpen(true)}
          aria-label={t('nav.open_menu')}
          className="nav-hamburger"
          style={{
            width: 32, height: 32, padding: 0,
            background: 'transparent', border: 'none',
            color: 'var(--t1)', cursor: 'pointer',
            borderRadius: 6,
            display: 'none', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0 }}>
          <div className="nav-brand-icon">PP</div>
          <span style={{
            fontFamily: 'var(--f)', fontWeight: 800, fontSize: 15,
            letterSpacing: '-.04em', color: 'var(--t0)',
          }}>
            MAP <span style={{ color: 'var(--blue)' }}>v5</span>
          </span>
        </div>

        {/* STI pill */}
        {sti !== undefined && (
          <div style={{
            fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700,
            padding: '3px 9px', borderRadius: 4, border: '1px solid',
            background: stiBg, color: stiColor, borderColor: stiBd,
            flexShrink: 0, letterSpacing: '.04em',
          }}>
            STI {sti}
          </div>
        )}

        {/* Rec pill — desktop only */}
        {rec && (
          <div style={{
            fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700,
            padding: '3px 9px', borderRadius: 4,
            background: recEntry.bg,
            color: recEntry.color,
            border: `1px solid ${recEntry.bd}`,
            flexShrink: 0,
          }} className="nav-rec-pill">
            {recLabel}
          </div>
        )}

        {/* Desktop tabs */}
        <div className="nav-tabs-desktop" style={{
          flex: 1, minWidth: 0, display: 'flex', gap: 2, overflow: 'auto',
          scrollbarWidth: 'none', msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                fontFamily: 'var(--f)', fontSize: 12,
                fontWeight: activeTab === tab.id ? 600 : 500,
                padding: '6px 12px', borderRadius: 0,
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid var(--blue)' : '2px solid transparent',
                background: 'transparent',
                color: activeTab === tab.id ? 'var(--blue)' : 'var(--t2)',
                cursor: 'pointer', transition: 'color .15s, border-color .15s',
                whiteSpace: 'nowrap', flexShrink: 0, letterSpacing: '.01em',
              }}
              onMouseEnter={e => { if(activeTab !== tab.id) { e.target.style.color='var(--t1)'; }}}
              onMouseLeave={e => { if(activeTab !== tab.id) { e.target.style.color='var(--t2)'; }}}
            >
              <span style={{ marginRight: 5, fontSize: 12 }}>{tab.icon}</span>
              {tabLabel(tab)}
            </button>
          ))}
        </div>

        {/* Mobile: empty flex spacer */}
        <div className="nav-mobile-spacer" style={{ flex: 1, display: 'none' }} />

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {lastSaved && (
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--t2)' }} className="nav-saved-time">
              {t('nav.saved_at', { time: lastSaved.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) })}
            </span>
          )}
          {canSave && (
            <button
              onClick={onSave}
              disabled={isSaving}
              style={{
                fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 600,
                padding: '0 12px', borderRadius: 6, height: 32,
                border: '1px solid',
                background: isSaving ? 'var(--green-l)' : 'var(--bg4)',
                borderColor: isSaving ? 'var(--green-b)' : 'var(--border)',
                color: isSaving ? 'var(--green)' : 'var(--t1)',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                transition: 'all .15s', letterSpacing: '.04em',
                minWidth: 80, textAlign: 'center',
                whiteSpace: 'nowrap', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center',
              }}
            >
              {isSaving ? t('nav.saving') : `↑ ${t('nav.save_run')}`}
            </button>
          )}

          {/* Language toggle (Position B — next to avatar, always visible) */}
          <LanguageToggle />

          {/* User avatar menu */}
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen(o => !o)}
              style={{
                width: 30, height: 30, borderRadius: '50%',
                background: role === 'admin' ? 'var(--violet)' : role === 'data_updater' ? 'var(--blue)' : 'var(--green)',
                color: 'white', border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 700, fontFamily: 'var(--f)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                letterSpacing: '.02em',
                boxShadow: menuOpen ? '0 0 0 2px var(--bg), 0 0 0 4px var(--blue)' : 'none',
                transition: 'box-shadow .15s', flexShrink: 0,
              }}
              title={user?.email}
            >
              {userInitials}
            </button>
            {menuOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow2)',
                minWidth: 220, padding: 6, zIndex: 1000,
              }}>
                <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t0)' }}>{userName}</div>
                  <div style={{ fontSize: 12, color: 'var(--t2)', marginTop: 2 }}>{user?.email}</div>
                  <div style={{ marginTop: 6 }}>
                    <span className={`badge badge-${roleColor}`}>{roleLabel}</span>
                  </div>
                </div>
                <button
                  onClick={() => { setMenuOpen(false); setShowChangePw(true); }}
                  style={{
                    width: '100%', textAlign: 'left',
                    padding: '8px 12px', border: 'none', borderRadius: 6,
                    background: 'transparent', color: 'var(--t1)',
                    fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    fontFamily: 'var(--f)',
                  }}
                  onMouseEnter={e => e.target.style.background = 'var(--bg3)'}
                  onMouseLeave={e => e.target.style.background = 'transparent'}
                >
                  🔑  {t('nav.menu_change_password')}
                </button>
                <button
                  onClick={() => { setMenuOpen(false); signOut(); }}
                  style={{
                    width: '100%', textAlign: 'left',
                    padding: '8px 12px', border: 'none', borderRadius: 6,
                    background: 'transparent', color: 'var(--red)',
                    fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    fontFamily: 'var(--f)',
                  }}
                  onMouseEnter={e => e.target.style.background = 'var(--red-l)'}
                  onMouseLeave={e => e.target.style.background = 'transparent'}
                >
                  ↪  {t('nav.menu_signout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 300,
              background: 'rgba(0,0,0,.6)',
              animation: 'fadeIn .2s ease',
            }}
          />
          <div style={{
            position: 'fixed', top: 0, left: 0, bottom: 0,
            width: '78%', maxWidth: 320,
            background: 'var(--bg2)',
            borderRight: '1px solid var(--border)',
            zIndex: 301,
            display: 'flex', flexDirection: 'column',
            animation: 'slideInLeft .25s ease',
            boxShadow: '4px 0 24px rgba(0,0,0,.5)',
          }}>
            {/* Drawer header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 18px', borderBottom: '1px solid var(--border)',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <div className="nav-brand-icon">PP</div>
                <span style={{
                  fontFamily: 'var(--f)', fontWeight: 800, fontSize: 15,
                  letterSpacing: '-.04em', color: 'var(--t0)',
                }}>
                  MAP <span style={{ color: 'var(--blue)' }}>v5</span>
                </span>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label={t('nav.close_menu')}
                style={{
                  width: 30, height: 30, borderRadius: 6,
                  background: 'var(--bg3)', border: '1px solid var(--border)',
                  color: 'var(--t1)', cursor: 'pointer', fontSize: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >×</button>
            </div>

            {/* Drawer tabs */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setDrawerOpen(false); }}
                  style={{
                    width: '100%', textAlign: 'left',
                    padding: '13px 18px', border: 'none',
                    background: activeTab === tab.id ? 'var(--blue-l)' : 'transparent',
                    borderLeft: activeTab === tab.id ? '3px solid var(--blue)' : '3px solid transparent',
                    color: activeTab === tab.id ? 'var(--blue)' : 'var(--t1)',
                    fontSize: 14,
                    fontWeight: activeTab === tab.id ? 600 : 500,
                    cursor: 'pointer', fontFamily: 'var(--f)',
                    display: 'flex', alignItems: 'center', gap: 12,
                    transition: 'background .12s',
                  }}
                  onTouchStart={e => { if (activeTab !== tab.id) e.currentTarget.style.background = 'var(--bg3)'; }}
                  onTouchEnd={e => { if (activeTab !== tab.id) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontSize: 18, width: 22, textAlign: 'center', flexShrink: 0 }}>{tab.icon}</span>
                  <span>{tabLabel(tab)}</span>
                </button>
              ))}
            </div>

            {/* Drawer footer — user info */}
            <div style={{
              borderTop: '1px solid var(--border)',
              padding: '14px 18px', flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{
                width: 34, height: 34, borderRadius: '50%',
                background: role === 'admin' ? 'var(--violet)' : role === 'data_updater' ? 'var(--blue)' : 'var(--green)',
                color: 'white',
                fontSize: 12, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {userInitials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t0)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userName}
                </div>
                <div style={{ marginTop: 3 }}>
                  <span className={`badge badge-${roleColor}`}>{roleLabel}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Change password modal */}
      {showChangePw && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={(e) => { if (e.target === e.currentTarget) setShowChangePw(false); }}>
          <div style={{ position: 'relative', maxWidth: 460, width: '90%' }}>
            <button
              onClick={() => setShowChangePw(false)}
              style={{
                position: 'absolute', top: 14, right: 14, zIndex: 10,
                width: 28, height: 28, borderRadius: '50%',
                background: 'var(--bg3)', border: '1px solid var(--border)',
                color: 'var(--t1)', cursor: 'pointer', fontSize: 14,
              }}
              aria-label="Close"
            >×</button>
            <SetPassword isInitial={false} />
          </div>
        </div>
      )}
    </>
  );
}
